import { Bento } from "../components/Bento";
import { t } from "@/i18n-js/instance";
import MembersAvatars from "./assets/members-avatars.png";
import MembersJoined from "./assets/members-joined.png";
import MemberGrowthRate from "./assets/member-growth-rate.png";
import WebsiteVisitsGrowth from "./assets/website-visits-growth.png";
import { SanitizeHTMLString } from "@/react/components/shared/uikit/SanitizeContent";
import { abbreviateNumber } from "@/react/helpers/number";
import isEmpty from "lodash/isEmpty";

const i18nRoot = "eoy_recap_2025.sections.community";

import type { CommunityGrowthMetrics } from "../../types";

interface CommunityProps {
  data?: CommunityGrowthMetrics;
}

export const Community = ({ data }: CommunityProps) => {
  if (!data) return null;

  return (
    <Bento
      id="community-growth"
      accentColor="#24287E"
      title={t([i18nRoot, "title"])}
    >
      <Bento.Item id="total-posts" baseColSpan={1} baseRowSpan={3}>
        <div className="flex flex-col h-full overflow-hidden">
          <div className="px-8 pt-8">
            <p className="text-4xl md:text-6xl font-bold">
              {abbreviateNumber(data.total_members)}
            </p>
            <p className="text-base font-medium leading-none tracking-[-0.25px] text-white mb-4">
              {t([i18nRoot, "bento_items.total_members"])}
            </p>
          </div>
          <div className="size-full flex items-end">
            <img
              src={MembersAvatars}
              alt={t([i18nRoot, "bento_items.total_members"])}
              className=""
              loading="lazy"
            />
          </div>
        </div>
      </Bento.Item>
      <Bento.Item id="total-courses-launched" baseColSpan={1} baseRowSpan={3}>
        <div className="relative flex flex-col h-full overflow-hidden">
          <div className="px-8 pt-8">
            <p className="text-4xl md:text-6xl font-bold">
              {abbreviateNumber(data.website_visits_growth)}
            </p>
            <p className="text-base font-medium leading-none tracking-[-0.25px] text-white">
              {t([i18nRoot, "bento_items.website_visits_and_growth"])}
            </p>
          </div>
          <img
            src={WebsiteVisitsGrowth}
            alt={t([i18nRoot, "bento_items.website_visits_and_growth"])}
            loading="lazy"
            className="absolute bottom-0 right-0 opacity-90"
          />
        </div>
      </Bento.Item>
      <Bento.Item
        id="top-performing-post-or-content-piece"
        baseColSpan={1}
        baseRowSpan={3}
      >
        <div className="flex flex-col gap-4 h-full p-8">
          <p className="text-4xl font-bold mb-2 leading-8">
            <SanitizeHTMLString
              content={t([i18nRoot, "bento_items.top_sources"])}
            />
          </p>
          {!isEmpty(data.top_sources) && (
            <div className="flex-1 min-h-0 flex flex-col gap-4 items-start justify-start">
              {data.top_sources?.map(source => (
                <div
                  key={source}
                  className="flex px-6 py-4 justify-center items-center rounded-full bg-[rgba(0,0,0,0.21)]"
                >
                  <p className="text-base font-medium leading-none tracking-[-0.25px] text-white">
                    {source}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </Bento.Item>
      <Bento.Item
        id="most-watched-or-attended-event"
        baseColSpan={2}
        baseRowSpan={2}
      >
        <div className="flex flex-row justify-between size-full overflow-hidden">
          <div className="px-8 pt-8 min-w-fit">
            <p className="text-4xl md:text-6xl font-bold">
              {data.member_growth_rate}%
            </p>
            <p className="text-base font-medium leading-none tracking-[-0.25px] text-white mb-4">
              {t([i18nRoot, "bento_items.member_growth_rate"])}
            </p>
          </div>
          <img
            src={MemberGrowthRate}
            alt={t([i18nRoot, "bento_items.member_growth_rate"])}
            loading="lazy"
            className="opacity-90 size-full object-cover absolute bottom-0 right-0 rounded-2xl md:relative"
          />
        </div>
      </Bento.Item>
      <Bento.Item id="new-members-joined" baseColSpan={1} baseRowSpan={2}>
        <div className="flex flex-col h-full items-start justify-between p-8">
          <div className="flex flex-col">
            <p className="text-4xl md:text-6xl font-bold">
              {abbreviateNumber(data.new_members_joined)}
            </p>
            <p className="text-base font-medium leading-none tracking-[-0.25px] text-white">
              {t([i18nRoot, "bento_items.new_members_joined"])}
            </p>
          </div>

          <img
            src={MembersJoined}
            alt={t([i18nRoot, "bento_items.new_members_joined"])}
            loading="lazy"
            className="size-36 mx-auto"
          />
        </div>
      </Bento.Item>
    </Bento>
  );
};
